import React from 'react'
import { Box, LinearProgress, Typography, useTheme } from '@mui/material'
import { styled } from '@mui/material/styles'

import _ from 'lodash'
import { valueFormUSDWithoutDecimalsForNonGrid } from '../../../common/commonFunctions'

type Item = {
  legend: string
  percent: number
  actualVal: string | number
  cpty: string
  cc_usage: number
  actualVal10K: number
}

type Props = {
  data: Item[]
}

// Styled rounded progress bar
const RoundedLinearProgress = styled(LinearProgress)(({ theme }) => ({
  height: 16,
  borderRadius: 8,
  backgroundColor: theme.palette.grey[200],
  overflow: 'hidden',

  '& .MuiLinearProgress-bar': {
    borderRadius: 8,
    transition: 'width 0.5s ease-in-out'
  }
}))

const LemurLDashboardCPTYRiskAllocUsage = ({ data }: Props) => {
  // const totalAlloc = _.sumBy(data, 'actualVal')
  const totalUsage = _.sumBy(data, 'cc_usage')

  type LegendItem = {
    name: string
    color: string
  }

  type LegendMarkerProps = {
    items: LegendItem[]
    size?: number
    gap?: number
  }

  const ApexLegendMarker: React.FC<LegendMarkerProps> = ({ items, size = 13, gap = 8 }) => {
    const theme = useTheme()

    return (
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: `${gap}px`
        }}
      >
        {items.map((item, index) => (
          <Box key={index} sx={{ display: 'flex', alignItems: 'center', mr: 2 }}>
            {/* Marker */}
            <Box
              sx={{
                width: size,
                height: size,
                borderRadius: '20%',
                backgroundColor: item.color,
                boxShadow: `0 0 0 1px ${theme.palette.background.paper}`,
                mr: 1
              }}
            />
            {/* Label */}
            <Typography variant='body2' sx={{ color: theme.palette.text.primary }}>
              {item.name}
            </Typography>
          </Box>
        ))}
      </Box>
    )
  }

  

  return (
    <>
      {data?.map((item, index) => {
        let barColor: 'success' | 'warning' | 'error' = 'success'
        if (item.percent > 50 && item.percent < 75) {
          barColor = 'warning'
        } else if (item.percent >= 75) {
          barColor = 'error'
        }

       

        return (
          <Box
            key={index}
            sx={{
              display: 'flex',
              alignItems: 'center',
              mb: index !== data.length - 1 ? 1 : 0
            }}
          >
            <Box
              sx={{
                ml: 4,
                width: '100%',
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                columnGap: 4,
                rowGap: 1
              }}
            >
              {/* LEFT: Legend */}
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  flexShrink: 0 // don't let legend shrink
                }}
              >
                <span title={item?.cpty}>
                  {item.cpty?.length > 15 ? item.cpty?.substring(0, 12) + '...' : item.cpty}
                </span>
              </Box>

              {/* RIGHT: Progress Bar + Label */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  flex: 1, // take remaining horizontal space
                  minWidth: 0,
                  justifyContent: 'flex-end'
                }}
              >
                {/* Bar fills the gap between legend and value */}
                <Box
                  sx={{
                    position: 'relative',
                    mr: 4,
                    flex: 1, // stretch to fill
                    minWidth: 0 // allow shrinking on small screens
                  }}
                >
                  <RoundedLinearProgress variant='determinate' value={item.percent} color={barColor} />
                  <Typography
                    title={`${valueFormUSDWithoutDecimalsForNonGrid(item?.cc_usage)}`}
                    variant='caption'
                    sx={{
                      position: 'absolute',
                      inset: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: 600
                    }}
                  >
                    {`${Math.round(item.percent)}%`}
                  </Typography>
                </Box>

                {/* Right value */}
                <Typography sx={{ color: 'text.disabled', flexShrink: 0 }} className='me-3'>
                  {valueFormUSDWithoutDecimalsForNonGrid(item.actualVal10K) + 'K'}
                </Typography>
              </Box>
            </Box>
          </Box>
        )
      })}

      <div className='d-flex flex-row mt-2 justify-content-center'>
        <div className='ms-2'>
          <div className='d-flex flex-row mt-2 justify-content-start'>
            <ApexLegendMarker items={[{ name: 'Total Usage', color: 'red' }]}></ApexLegendMarker>
            <Typography className='ms-2'>{valueFormUSDWithoutDecimalsForNonGrid(totalUsage)}</Typography>
          </div>
        </div>
        <div className='d-flex flex-row mt-2 justify-content-end ms-4'>
          <ApexLegendMarker items={[{ name: 'Total Alloc', color: 'green' }]}></ApexLegendMarker>
          <Typography className='ms-2'>$20,000,000</Typography>
        </div>
      </div>
    </>
  )
}

export default LemurLDashboardCPTYRiskAllocUsage
